/**
 * ConfirmDialog
 * Testable confirmation dialog replacing native Alert.alert with buttons.
 * Uses Modal for overlay. All elements have testIDs for Maestro testing.
 */

import React from 'react';
import { Modal, TouchableOpacity, StyleSheet, View } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { colors as appColors, FS_SM, FS_MD, FS_LG } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface ConfirmDialogProps {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button (for destructive actions like delete) */
  destructive?: boolean;
  /** Hide cancel button (for acknowledgment-only dialogs) */
  confirmOnly?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  visible,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  confirmOnly = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View
        testID="confirm-dialog-overlay"
        style={styles.overlay}
      >
        <View
          testID="confirm-dialog"
          accessibilityRole="alert"
          style={[styles.card, { backgroundColor: theme.card }]}
        >
          <Text
            testID="confirm-dialog-title"
            allowFontScaling={false}
            style={[styles.title, { fontSize: FS_LG * fontScale, color: theme.text, ...ffs }]}
          >
            {title}
          </Text>
          <Text
            testID="confirm-dialog-message"
            allowFontScaling={false}
            style={[styles.message, { fontSize: FS_MD * fontScale, color: theme.textSecondary, ...ffs }]}
          >
            {message}
          </Text>
          <View style={styles.buttons}>
            {!confirmOnly && (
              <TouchableOpacity
                testID="confirm-dialog-cancel"
                accessibilityRole="button"
                accessibilityLabel={cancelLabel || 'Cancel'}
                onPress={onCancel}
                style={[styles.button, { borderColor: theme.border, borderWidth: 1 }]}
              >
                <Text
                  allowFontScaling={false}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  style={[styles.buttonText, { fontSize: FS_SM * fontScale, color: theme.text, ...ffs }]}
                >
                  {cancelLabel || 'Cancel'}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              testID="confirm-dialog-confirm"
              accessibilityRole="button"
              accessibilityLabel={confirmLabel || 'OK'}
              onPress={onConfirm}
              style={[
                styles.button,
                {
                  backgroundColor: destructive ? theme.error : appColors.primary,
                },
              ]}
            >
              <Text
                allowFontScaling={false}
                numberOfLines={1}
                adjustsFontSizeToFit
                style={[styles.buttonText, { fontSize: FS_SM * fontScale, color: '#FFFFFF', fontWeight: '600', ...ffs }]}
              >
                {confirmLabel || 'OK'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  card: {
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 340,
  },
  title: {
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  message: {
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  buttonText: {
    fontWeight: '500',
  },
});
